import React from "react";
import {Link as RouterLink} from "react-router-dom";
import {Box, Button, Container, Flex, Heading, Link, SimpleGrid, Stack, Text, VStack} from "@chakra-ui/react";
import {EmailIcon, PhoneIcon} from "@chakra-ui/icons";
import {categories, categoryUrl} from "../data/catalog";
import {site} from "../data/site";
import {navLinks} from "./Header";
import {useQuote} from "./QuoteContext";
import Logo from "./Logo";

const linkStyle = {color: "whiteAlpha.700", fontSize: "sm", _hover: {color: "white", textDecoration: "none"}};

function FooterHeading({children}) {
    return (
        <Heading as="h3" fontSize="sm" color="white" textTransform="uppercase" letterSpacing="0.12em" mb={5}>
            {children}
        </Heading>
    );
}

// Kresta-style "can't find what you're looking for" band, shown above the footer on inner pages.
export function CtaBand() {
    const {openQuote} = useQuote();
    return (
        <Box bg="brand.500" color="white">
            <Container maxW="container.xl" py={{base: 10, md: 12}}>
                <Flex direction={{base: "column", md: "row"}} align={{base: "start", md: "center"}} justify="space-between" gap={6}>
                    <Box>
                        <Heading as="h2" color="white" fontSize={{base: "xl", md: "2xl"}} textTransform="uppercase" letterSpacing="0.03em">
                            Can't find what you are looking for?
                        </Heading>
                        <Text mt={2} color="whiteAlpha.900">
                            Everything we do is custom made. Tell us about your project and we will quote it for free.
                        </Text>
                    </Box>
                    <Stack direction={{base: "column", sm: "row"}} spacing={3} flexShrink={0} w={{base: "100%", md: "auto"}}>
                        <Button as="a" href={site.phoneHref} variant="outline" colorScheme="whiteAlpha" color="white"
                                borderColor="whiteAlpha.700" leftIcon={<PhoneIcon/>} _hover={{bg: "whiteAlpha.200"}}>
                            {site.phone}
                        </Button>
                        <Button bg="white" color="brand.600" _hover={{bg: "ink.50"}} onClick={() => openQuote()}>
                            Book a free quote
                        </Button>
                    </Stack>
                </Flex>
            </Container>
        </Box>
    );
}

function Footer() {
    return (
        <Box as="footer" bg="ink.900" color="whiteAlpha.700">
            <Container maxW="container.xl" py={{base: 12, md: 16}}>
                <SimpleGrid columns={{base: 1, md: 2, lg: 12}} spacing={{base: 10, lg: 8}}>
                    <Box gridColumn={{lg: "span 4"}}>
                        <Logo light/>
                        <Text mt={5} fontSize="sm" maxW="sm" lineHeight={1.7}>
                            English and Australian background in carpentry and joinery, with more than 20 years of experience
                            providing highly qualified and licensed carpentry and joinery services.
                        </Text>
                    </Box>
                    <Box gridColumn={{lg: "span 4"}}>
                        <FooterHeading>Products</FooterHeading>
                        <SimpleGrid columns={2} spacingX={6} spacingY={2.5}>
                            {categories.map((category) => (
                                <Link key={category.path} as={RouterLink} to={categoryUrl(category)} {...linkStyle}>
                                    {category.title}
                                </Link>
                            ))}
                        </SimpleGrid>
                    </Box>
                    <Box gridColumn={{lg: "span 2"}}>
                        <FooterHeading>Company</FooterHeading>
                        <VStack align="start" spacing={2.5}>
                            {navLinks.map((item) => (
                                <Link key={item.label} as={RouterLink} to={item.to} {...linkStyle}>{item.label}</Link>
                            ))}
                        </VStack>
                    </Box>
                    <Box gridColumn={{lg: "span 2"}}>
                        <FooterHeading>Contact</FooterHeading>
                        <VStack align="start" spacing={2.5} fontSize="sm">
                            <Link href={site.phoneHref} {...linkStyle} display="flex" alignItems="center" gap={2}>
                                <PhoneIcon/> {site.phone}
                            </Link>
                            <Link href={`mailto:${site.email}`} {...linkStyle} display="flex" alignItems="center" gap={2} wordBreak="break-all">
                                <EmailIcon/> {site.email}
                            </Link>
                            <Text pt={2}>ABN {site.abn}</Text>
                            <Text>Licence No. {site.license}</Text>
                        </VStack>
                    </Box>
                </SimpleGrid>
            </Container>
            <Box borderTop="1px solid" borderColor="whiteAlpha.200">
                <Container maxW="container.xl" py={5} fontSize="xs" display="flex" flexDirection={{base: "column", md: "row"}}
                           gap={2} justifyContent="space-between">
                    <Text>© {new Date().getFullYear()} {site.name}. All rights reserved.</Text>
                    <Text>Qualified & licensed carpentry and joinery</Text>
                </Container>
            </Box>
        </Box>
    );
}

export default Footer;
